import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  giftcardCaptchaRequired,
  giftcardFinalPrice,
  type GiftcardTextField,
} from '../../state/giftcard';
import { Field } from '../fields/Field';
import { fieldId, moneyFormatter } from '../format';
import { LockIcon } from '../icons';
import { Recaptcha } from '../Recaptcha';
import { Section } from '../Section';
import { useGiftEnv, useGiftState } from './context';

/**
 * All headings/labels here are ours (hardcoded): unlike the booking form there is no
 * tenant-editable copy endpoint for gift cards — wording changes go through us.
 */

function TextField(props: {
  field: GiftcardTextField;
  label: string;
  type?: string;
  placeholder?: string;
  multiline?: boolean;
  help?: string;
}) {
  const { controller } = useGiftEnv();
  const state = useGiftState();
  const key = props.field === 'discountCode' ? 'discount_code' : props.field;
  const errors = controller.visibleErrors(key);
  return (
    <Field fieldKey={key} label={props.label} required errors={errors} help={props.help}>
      {({ id, describedBy, invalid }) =>
        props.multiline ? (
          <textarea
            id={id}
            className="cbf-textarea"
            rows={3}
            value={state.values[props.field]}
            placeholder={props.placeholder}
            aria-required="true"
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            onChange={(e) => controller.setField(props.field, e.target.value)}
            onBlur={() => controller.blur(key)}
          />
        ) : (
          <input
            id={id}
            className="cbf-input"
            type={props.type ?? 'text'}
            value={state.values[props.field]}
            placeholder={props.placeholder}
            aria-required="true"
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            onChange={(e) => controller.setField(props.field, e.target.value)}
            onBlur={() => controller.blur(key)}
          />
        )
      }
    </Field>
  );
}

function AmountPicker() {
  const { controller } = useGiftEnv();
  const state = useGiftState();
  const money = moneyFormatter(state.settings);
  const amounts = state.giftcards?.amounts ?? [];
  const errors = controller.visibleErrors('amount');
  return (
    <Section heading="CHOOSE AN AMOUNT">
      <Field fieldKey="amount" label="Gift card amount" required errors={errors} group>
        {() => (
          <div className="cbf-pills" role="radiogroup" aria-label="Gift card amount">
            {amounts.map((a) => (
              <label key={a} className={`cbf-pill${state.values.amount === a ? ' is-on' : ''}`}>
                <input
                  type="radio"
                  name="cbf-gift-amount"
                  checked={state.values.amount === a}
                  onChange={() => controller.selectAmount(a)}
                  onBlur={() => controller.blur('amount')}
                />
                {money(a)}
              </label>
            ))}
          </div>
        )}
      </Field>
    </Section>
  );
}

function DiscountField() {
  const { controller } = useGiftEnv();
  const state = useGiftState();
  const errors = controller.visibleErrors('discount_code');
  const applied = state.discount.applied;
  if (applied) {
    return (
      <div className="cbf-field" data-cbf-field="discount_code">
        <span className="cbf-label">Discount code</span>
        <p className="cbf-callout">
          <strong>{applied.code}</strong> — {applied.percent}% off
          {applied.message ? ` · ${applied.message}` : ''}{' '}
          <button
            type="button"
            className="cbf-btn cbf-btn-secondary cbf-btn-sm"
            onClick={() => controller.clearDiscount()}
          >
            Remove
          </button>
        </p>
      </div>
    );
  }
  return (
    <Field fieldKey="discount_code" label="Discount code" errors={errors} help="Optional">
      {({ id, describedBy, invalid }) => (
        <div className="cbf-gift-discount">
          <input
            id={id}
            className="cbf-input"
            type="text"
            value={state.values.discountCode}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            onChange={(e) => controller.setField('discountCode', e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void controller.applyDiscount();
              }
            }}
          />
          <button
            type="button"
            className="cbf-btn cbf-btn-secondary"
            disabled={state.discount.status === 'checking' || !state.values.discountCode.trim()}
            onClick={() => void controller.applyDiscount()}
          >
            {state.discount.status === 'checking' ? 'Checking…' : 'Apply'}
          </button>
        </div>
      )}
    </Field>
  );
}

function CardField() {
  const { controller, stripe } = useGiftEnv();
  const status = useSyncExternalStore(stripe.subscribe, stripe.status, stripe.status);
  const host = useRef<HTMLDivElement>(null);
  const [focus, setFocus] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (status !== 'ready' || !host.current) return;
    const unmount = stripe.mountCard(host.current, ({ complete, error }) => {
      setLocalError(error);
      controller.cardState(complete);
    });
    const el = host.current;
    const onFocus = () => setFocus(true);
    const onBlur = () => {
      setFocus(false);
      controller.blur('card');
    };
    el.addEventListener('focusin', onFocus);
    el.addEventListener('focusout', onBlur);
    return () => {
      unmount();
      el.removeEventListener('focusin', onFocus);
      el.removeEventListener('focusout', onBlur);
    };
  }, [status, stripe, controller]);

  const errors = Array.from(
    new Set([...(localError ? [localError] : []), ...controller.visibleErrors('card')]),
  );
  return (
    <Field fieldKey="card" label="Card details" required errors={errors} group>
      {({ invalid }) => (
        <>
          {status === 'unavailable' ? (
            <p className="cbf-error" role="alert">
              Card payments are unavailable right now. Please try again later.
            </p>
          ) : (
            <div
              className={`cbf-stripe${focus ? ' is-focus' : ''}${invalid ? ' is-invalid' : ''}`}
              data-cbf-field="card"
            >
              {status === 'ready' ? (
                <div ref={host} />
              ) : (
                <span className="cbf-help">Loading secure card form…</span>
              )}
            </div>
          )}
          <p className="cbf-secure">
            <LockIcon />
            <span>Secured by Stripe</span>
          </p>
        </>
      )}
    </Field>
  );
}

function UnavailableNotice() {
  return (
    <div className="cbf-card" role="alert">
      <h2 className="cbf-h2">Gift cards aren&rsquo;t available right now</h2>
      <p className="cbf-p">Please check back soon, or contact us and we&rsquo;ll help you out.</p>
    </div>
  );
}

function GiftSuccess() {
  const state = useGiftState();
  const money = moneyFormatter(state.settings);
  const amount = state.values.amount ?? 0;
  const percent = state.discount.applied?.percent ?? 0;
  return (
    <section className="cbf-card cbf-success" role="status" aria-live="polite">
      <h2 className="cbf-h2">Gift card sent!</h2>
      <p className="cbf-p">
        A {money(amount)} gift card is on its way to{' '}
        <strong>{state.values.recipient_email.trim()}</strong>. We&rsquo;ve emailed a receipt to{' '}
        <strong>{state.values.sender_email.trim()}</strong>.
        {state.result?.id ? ` Your reference is #${state.result.id}.` : ''}
      </p>
      {percent > 0 ? (
        <p className="cbf-help">
          You paid {money(giftcardFinalPrice(amount, percent))} ({percent}% off).
        </p>
      ) : null}
    </section>
  );
}

export function GiftCardApp() {
  const { controller, config } = useGiftEnv();
  const state = useGiftState();
  const money = moneyFormatter(state.settings);

  useEffect(() => {
    if (!state.focusKey) return;
    const key = state.focusKey;
    const el =
      document.getElementById(fieldId(key)) ??
      document.querySelector<HTMLElement>(
        `[data-cbf-field="${key}"] input, [data-cbf-field="${key}"] textarea, [data-cbf-field="${key}"] button, [data-cbf-field="${key}"]`,
      );
    if (el) {
      el.focus({ preventScroll: true });
      el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }
    controller.focused();
  }, [state.focusKey, controller]);

  if (state.status === 'booting') {
    return (
      <div className="cbf-root">
        <p className="cbf-status" role="status">
          Loading gift cards…
        </p>
      </div>
    );
  }
  if (state.status === 'boot-failed') {
    return (
      <div className="cbf-root">
        <div className="cbf-card" role="alert">
          <h2 className="cbf-h2">Gift cards aren&rsquo;t available right now</h2>
          <p className="cbf-p">Please refresh the page or try again in a few minutes.</p>
        </div>
      </div>
    );
  }
  if (state.status === 'unavailable') {
    return (
      <div className="cbf-root">
        <UnavailableNotice />
      </div>
    );
  }
  if (state.status === 'succeeded') {
    return (
      <div className="cbf-root cbf-gift">
        <GiftSuccess />
      </div>
    );
  }

  const amount = state.values.amount;
  const percent = state.discount.applied?.percent ?? 0;
  const total = amount !== null ? giftcardFinalPrice(amount, percent) : null;
  const submitting = state.status === 'submitting';
  // Always shown: the API rejects a gift card without a verified token (see giftcardCaptchaRequired).
  const captchaVisible = Boolean(config.recaptchaSiteKey);

  return (
    <div className="cbf-root cbf-gift">
      <header className="cbf-header">
        <h1 className="cbf-title">Gift Cards</h1>
        <p className="cbf-lead">Give the gift of a spotless ride — delivered instantly by email.</p>
      </header>
      {state.banner.length ? (
        <div className="cbf-banner" role="alert">
          {state.banner.map((msg) => (
            <p key={msg}>{msg}</p>
          ))}
        </div>
      ) : null}
      <form
        className="cbf-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void controller.submit();
        }}
      >
        <AmountPicker />
        <Section heading="WHO IS IT FOR?">
          <div className="cbf-row cbf-row-2">
            <TextField field="recipient_name" label="Recipient's Name" placeholder="Jordan Smith" />
            <TextField
              field="recipient_email"
              label="Recipient's Email"
              type="email"
              placeholder="jordan@example.com"
            />
          </div>
          <TextField
            field="message"
            label="Personal Message"
            multiline
            placeholder="Enjoy a fresh, spotless ride — on me!"
            help="Tip: You can enter your own email as the recipient to print off and give at a later date."
          />
        </Section>
        <Section heading="YOUR DETAILS">
          <div className="cbf-row cbf-row-2">
            <TextField field="sender_name" label="Your Name" />
            <TextField field="sender_email" label="Your Email" type="email" />
          </div>
        </Section>
        <Section heading="PAYMENT">
          {state.giftcards?.show_discount ? <DiscountField /> : null}
          <CardField />
          {captchaVisible ? (
            <Recaptcha
              siteKey={config.recaptchaSiteKey}
              errors={controller.visibleErrors('recaptcha_token')}
              onToken={(token) => controller.recaptcha(token)}
            />
          ) : null}
          {amount !== null && percent > 0 && total !== null ? (
            <p className="cbf-p cbf-gift-total">
              {money(amount)} − {percent}% = <strong>{money(total)}</strong>
            </p>
          ) : null}
          <button type="submit" className="cbf-btn cbf-btn-block" disabled={submitting}>
            {submitting
              ? 'Processing…'
              : total !== null
                ? `Buy Gift Card — ${money(total)}`
                : 'Buy Gift Card'}
          </button>
          <p className="cbf-footer-note">
            Your card is charged now and the gift card is emailed to the recipient right away.
          </p>
        </Section>
      </form>
    </div>
  );
}
